// RawEventJsonPanel
// Collapsible pretty-printed view of an event's raw JSON payload, with a hover-reveal
// CopyButton. Supports two sources:
//   - inline `rawEventJson` (a JSON string ≤ 32 KB per the ATS schema).
//   - `blobPath` + `fetchBlob` callback (for payloads spilled to the event-evidence blob).
// When `blobPath` is set (and `rawEventJson` is empty), the panel lazy-fetches the
// blob the first time the section is expanded. Failed fetches surface an amber chip
// with a retry button rather than breaking the row.
//
// Used by RecentEventsPage. Will replace the inline JSON viewer on AlertsPage +
// TestRunResultsPage in Phase 2 (see CLAUDE.md three-way display parity rule).

import React, { useState, useCallback, useEffect } from 'react';
import { CollapsibleSection } from './sharedHelpers';

function tryPretty(jsonString) {
  if (!jsonString) return '';
  try {
    return JSON.stringify(JSON.parse(jsonString), null, 2);
  } catch {
    return String(jsonString);
  }
}

export default function RawEventJsonPanel({ rawEventJson, blobPath, fetchBlob, title = 'Raw Event JSON' }) {
  const inlineAvailable = !!rawEventJson;
  const needsFetch = !inlineAvailable && !!blobPath;

  const [open, setOpen] = useState(false);
  const [fetched, setFetched] = useState(null);   // string when loaded, null = not yet attempted
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  const loadBlob = useCallback(async () => {
    if (!fetchBlob || !blobPath) return;
    setFetching(true);
    setError(null);
    try {
      const payload = await fetchBlob(blobPath);
      // API may return a parsed object or a raw string. Normalise to string so the
      // pretty-printer + CopyButton both work.
      const asString = typeof payload === 'string' ? payload : JSON.stringify(payload);
      setFetched(asString);
    } catch (err) {
      setError(err?.message || 'Failed to load event source from blob.');
    } finally {
      setFetching(false);
    }
  }, [blobPath, fetchBlob]);

  // Trigger the blob fetch automatically the first time the panel opens.
  useEffect(() => {
    if (open && needsFetch && fetched === null && !fetching && !error) {
      loadBlob();
    }
  }, [open, needsFetch, fetched, fetching, error, loadBlob]);

  const jsonString = inlineAvailable ? rawEventJson : fetched;
  const pretty = jsonString ? tryPretty(jsonString) : '';

  const handleCopy = (e) => {
    e.stopPropagation();
    if (!pretty) return;
    navigator.clipboard.writeText(pretty).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    });
  };

  // Visible inline Copy button next to the section title — same shape and feedback
  // as AlertsPage.jsx's CollapsibleJson copy button. Only rendered when there's
  // actually content available to copy.
  const headerExtra = pretty ? (
    <button
      type="button"
      onClick={handleCopy}
      className={`ml-1 text-[10px] px-1.5 py-0.5 rounded border transition-colors ${
        copied ? 'border-green-600 text-green-400' : 'border-gray-600 text-gray-400 hover:text-gray-200 hover:border-gray-400'
      }`}
    >
      {copied ? 'Copied!' : 'Copy'}
    </button>
  ) : null;

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <CollapsibleSection
        title={title}
        open={open}
        onToggle={setOpen}
        headerClassName="text-gray-400"
        headerExtra={headerExtra}
      >
        <div className="space-y-2">
          {needsFetch && (
            <div className="flex items-center gap-2 text-[11px] text-gray-500 italic">
              <span>Payload spilled to event-evidence blob.</span>
              {fetching && <span className="text-blue-400">loading…</span>}
            </div>
          )}
          {error && (
            <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded border border-amber-700/60 bg-amber-900/30 text-amber-300 text-xs">
              <span>{error}</span>
              <button
                type="button"
                onClick={loadBlob}
                className="ml-1 underline hover:text-amber-200"
              >retry</button>
            </div>
          )}
          {pretty && (
            <pre className="text-[11px] font-mono text-gray-300 bg-gray-950/60 border border-gray-800 rounded p-2 max-h-96 overflow-auto whitespace-pre-wrap break-all">
              {pretty}
            </pre>
          )}
          {!pretty && !fetching && !error && needsFetch && (
            <button
              type="button"
              onClick={loadBlob}
              className="text-[11px] text-blue-400 hover:text-blue-300 underline"
            >Load raw JSON from blob</button>
          )}
          {!pretty && !fetching && !error && !needsFetch && !inlineAvailable && (
            <div className="text-[11px] text-gray-500 italic">No raw JSON available for this event.</div>
          )}
        </div>
      </CollapsibleSection>
    </div>
  );
}
